import {
  CreatePetPayload,
  Pet,
  UpdatePetPayload,
  debouncedCreatePet,
  deletePet,
  getPets,
  updatePet,
} from 'api/pets';
import { create } from 'zustand';

interface ApiState {
  pets: Pet[] | null;
  isLoading: boolean;
  error: Error | null;
}

interface ApiActions {
  fetchPets: (customerId: string) => Promise<void>;
  createPet: (payload: UpdatePetPayload) => Promise<Pet>;
  updatePet: (payload: UpdatePetPayload) => Promise<void>;
  deletePet: (petId: string) => Promise<void>;
  resetError: () => void;
}

export const usePetProfilesApiStore = create<ApiState & ApiActions>()(
  (set) => ({
    // State
    pets: null,
    isLoading: false,
    error: null,

    // Actions
    fetchPets: async (customerId: string) => {
      try {
        set({ isLoading: true });
        const pets = await getPets(parseInt(customerId, 10));
        set({ pets, error: null });
      } catch (error) {
        set({ error: error as Error });
      } finally {
        set({ isLoading: false });
      }
    },

    createPet: async (payload: CreatePetPayload) => {
      try {
        set({ isLoading: true });
        const pet = await debouncedCreatePet(payload);
        set((state) => ({
          pets: state.pets ? [...state.pets, pet] : [pet],
          error: null,
        }));
        return pet;
      } catch (error) {
        set({ error: error as Error });
        throw error;
      } finally {
        set({ isLoading: false });
      }
    },

    updatePet: async (payload: UpdatePetPayload) => {
      try {
        set({ isLoading: true });
        await updatePet(payload);
        set((state) => ({
          pets:
            state.pets?.map((p) =>
              p.id === payload.id ? { ...p, ...payload } : p
            ) ?? null,
          error: null,
        }));
      } catch (error) {
        set({ error: error as Error });
        throw error;
      } finally {
        set({ isLoading: false });
      }
    },

    deletePet: async (petId: string) => {
      try {
        set({ isLoading: true });
        await deletePet(petId);
        set((state) => ({
          pets: state.pets?.filter((p) => p.id !== petId) ?? null,
          error: null,
        }));
      } catch (error) {
        set({ error: error as Error });
        throw error;
      } finally {
        set({ isLoading: false });
      }
    },

    resetError: () => set({ error: null }),
  })
);
